import {
  Activity as ActivityIcon,
  Bot,
  Calendar,
  CheckCircle,
  Clock,
  Sparkles,
  Target,
  TrendingUp,
  Zap,
  Info,
} from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import apiService from "../shared/apiService";
import {
  Card,
  PageLayout,
  SectionHeader,
  StatsBar,
  Tooltip,
} from "../components";
import PostRanking, {
  PlatformDashboard,
  SortMetric,
  RankedPost,
  buildPlatformStats,
  GmbMetrics,
} from "../components/analytics/PostRanking";
import { PostsAnalyticsSkeleton } from "../components/skeletons";
import { useActiveContext } from "../contexts/ActiveContextContext";
import { useTranslation } from "../contexts/LanguageContext";
import { usePosts } from "../hooks/usePosts";
import postsService from "../services/postsService";
import { cn } from "../utils/cn";

type PeriodKey = "7d" | "30d" | "90d" | "365d" | "all";

interface ScheduleOverview {
  scheduled_posts: any[];
  statistics: {
    total_posts: number;
    total_platforms: number;
    platforms: string[];
    posts_per_week: { [week: string]: number };
    average_posts_per_week: number;
    date_range: {
      start: string | null;
      end: string | null;
    };
  };
}

const PostsAnalytics: React.FC = () => {
  const { t } = useTranslation();
  const { activeContext } = useActiveContext();

  const PERIODS = useMemo(
    () => [
      { key: "7d" as PeriodKey, label: t("7 dias"), days: 7 },
      { key: "30d" as PeriodKey, label: t("30 dias"), days: 30 },
      { key: "90d" as PeriodKey, label: t("90 dias"), days: 90 },
      { key: "365d" as PeriodKey, label: t("1 ano"), days: 365 },
      { key: "all" as PeriodKey, label: t("Todo período"), days: null },
    ],
    [t],
  );

  const {
    stats,
    loading: postsHookLoading,
    loadPosts,
    scheduledPosts,
    approvedPosts,
    pendingPosts,
    publishedPosts,
  } = usePosts();

  const [scheduleOverview, setScheduleOverview] =
    useState<ScheduleOverview | null>(null);
  const [scheduleLoading, setScheduleLoading] = useState(true);
  const [period, setPeriod] = useState<PeriodKey>("90d");
  const [gmbData, setGmbData] = useState<GmbMetrics | null>(null);
  const [gmbLoading, setGmbLoading] = useState(false);

  // Estados elevados do Ranking de Posts
  const [posts, setPosts] = useState<RankedPost[]>([]);
  const [postsLoading, setPostsLoading] = useState(true);
  const [postsError, setPostsError] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<SortMetric>("engagement_rate");

  const activePeriod = PERIODS.find((p) => p.key === period)!;
  const dateFrom = activePeriod.days
    ? new Date(Date.now() - activePeriod.days * 86400_000)
    : null;
  const dateTo = new Date();

  // Função para buscar posts publicados com métricas (para Ranking + Platform)
  // Não depende de sortBy — a ordenação é feita localmente no sortedPosts useMemo.
  const fetchPosts = useCallback(async () => {
    setPostsLoading(true);
    setPostsError(null);
    try {
      const response = await apiService.get<any>("/api/posts", {
        status: "published",
        limit: 200,
        has_metrics: true,
      });
      const raw: any[] =
        response?.posts || response?.data?.posts || response?.data || [];

      // Filtro: manter apenas posts com pelo menos uma métrica não-nula
      const withMetrics = raw.filter(
        (p) =>
          p.metrics &&
          Object.values(p.metrics).some(
            (v) => v !== null && v !== undefined && v !== 0,
          ),
      );
      setPosts(withMetrics as RankedPost[]);
    } catch (e: any) {
      setPostsError(e.message || "Erro ao carregar posts");
    } finally {
      setPostsLoading(false);
    }
  }, [
    activeContext.type,
    activeContext.organization?.id,
    activeContext.clientCompany?.id,
  ]);

  // Carregar dados do Google Meu Negócio
  const loadGmb = useCallback(() => {
    const p = PERIODS.find((x) => x.key === period)!;
    setGmbData(null);
    setGmbLoading(true);
    apiService
      .get<any>("/api/integrations/google/performance", { days: p.days ?? 540 })
      .then((r) => {
        if (r?.success) setGmbData(r as GmbMetrics);
      })
      .catch(() => {})
      .finally(() => setGmbLoading(false));
  }, [period, PERIODS]);

  const loadScheduleData = useCallback(async () => {
    setScheduleLoading(true);
    try {
      const data = await postsService.getScheduleOverview();
      setScheduleOverview(data);
    } catch (error) {
      console.error("Erro ao carregar dados de agendamento:", error);
    } finally {
      setScheduleLoading(false);
    }
  }, []);

  // Efeito único: dispara os 3 carregamentos em paralelo ao montar / trocar contexto.
  // Isso reduz o tempo total de "página em branco" ao eliminar a sequência serial.
  useEffect(() => {
    setScheduleOverview(null);
    setScheduleLoading(true);
    // Promise.all: loadPosts (stats), fetchPosts (ranking) e loadScheduleData em paralelo.
    // loadGmb gerencia seu próprio estado de loading e é independente.
    Promise.all([loadPosts(), fetchPosts(), loadScheduleData()]);
    loadGmb();
  }, [
    activeContext.type,
    activeContext.organization?.id,
    activeContext.clientCompany?.id,
    loadPosts,
    fetchPosts,
    loadScheduleData,
    loadGmb,
  ]);

  // Ordenar posts localmente por sortBy (sem requery ao backend)
  const sortedPosts = useMemo(() => {
    const metric = sortBy as string;
    return posts
      .filter((p) => {
        if (!p.published_at) return true;
        const d = new Date(p.published_at);
        if (dateFrom && d < dateFrom) return false;
        if (dateTo && d > dateTo) return false;
        return true;
      })
      .sort((a, b) => {
        const aVal = (a.metrics as any)?.[metric] ?? -1;
        const bVal = (b.metrics as any)?.[metric] ?? -1;
        return bVal - aVal;
      });
  }, [posts, dateFrom, dateTo, sortBy]);

  // Estatísticas das plataformas
  const platformStats = useMemo(
    () => buildPlatformStats(sortedPosts),
    [sortedPosts],
  );

  // Loading geral da página
  const pageLoading = postsHookLoading || postsLoading;

  return (
    <PageLayout
      title={t("Performance de Canais")}
      subtitle={t(
        "Métricas de distribuição, eficácia de IA e saúde da grade de conteúdo em tempo real.",
      )}
      containerSize="full"
      showContainer={true}
      padding="none"
      className="w-full px-2! py-2!"
      loading={pageLoading}
      skeleton={<PostsAnalyticsSkeleton />}
      actions={
        // Fica na description bar (fora da área com scroll) porque é um
        // status passivo da página inteira, não um controle de uma seção
        // específica — precisa continuar visível mesmo com a página rolada.
        <div className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-green-50 border border-green-200">
          <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
          <span className="text-[10px] font-black text-green-700 uppercase tracking-wider">
            {t("Dados Sincronizados")}
          </span>
        </div>
      }
    >
      <div className="w-full flex flex-col gap-2">
        {/* Visão Geral da Atividade - Pipeline de Conteúdo (Fixo no Topo) */}
        <div className="shrink-0 flex flex-col gap-2 bg-white rounded-xl border border-purple-200 p-3 shadow-xs">
          <SectionHeader
            icon={<ActivityIcon className="h-5 w-5" />}
            title={t("Métricas de Pipeline")}
            subtitle={t("Fluxo de Conteúdo")}
            iconBgClass="bg-purple-600"
            action={
              // Seletor de Período — o período já filtra sortedPosts (ver
              // dateFrom/dateTo); este controle apenas expõe na UI um estado
              // que já existia. Fica aqui (não na description bar) porque é
              // um controle de dado, não um status passivo — precisa estar
              // visualmente colado ao conteúdo que ele filtra.
              <div className="flex items-center bg-purple-50/60 border border-purple-100 rounded-lg p-0.5 gap-0.5 self-center">
                {PERIODS.map((p) => (
                  <button
                    key={p.key}
                    onClick={() => setPeriod(p.key)}
                    className={cn(
                      "px-2.5 py-1 rounded-md text-xs font-bold transition-all focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-purple-500",
                      period === p.key
                        ? "bg-white text-purple-700 shadow-xs"
                        : "text-gray-500 hover:text-gray-700",
                    )}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            }
          />

          <StatsBar
            stats={{
              total_posts:
                (stats?.pending || 0) +
                (stats?.approved || 0) +
                (stats?.published || 0) +
                (stats?.rejected || 0) +
                (stats?.scheduled || 0),
              pending_approval: stats?.pending || 0,
              approved: stats?.approved || 0,
              rejected: stats?.rejected || 0,
              published: stats?.published || 0,
              approval_rate: (() => {
                const approved = stats?.approved || 0;
                const rejected = stats?.rejected || 0;
                const totalEvaluated = approved + rejected;
                return totalEvaluated > 0
                  ? (approved / totalEvaluated) * 100
                  : 0;
              })(),
            }}
          />
        </div>

        {/* Nova Grade de Dashboard estruturada em Linhas Horizontais */}
        <div className="flex flex-col gap-2 w-full">
          {/* Linha 1: Eficácia da Inteligência (8/12) + Calendário de Distribuição (4/12) */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-2 w-full">
            {/* Seção: Eficácia da Inteligência */}
            <div className="lg:col-span-8 flex flex-col gap-2 bg-white rounded-xl border border-purple-200 p-3 shadow-xs">
              <SectionHeader
                icon={<Bot className="h-5 w-5" />}
                title={t("Eficácia da Inteligência")}
                subtitle={t("Social Automation")}
                iconBgClass="bg-purple-600"
              />

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-stretch flex-1">
                {/* Card de IA Gen */}
                <div className="relative group overflow-hidden bg-linear-to-br from-indigo-600 to-purple-700 rounded-xl p-3 text-white shadow-sm transition-all flex flex-col justify-between min-h-[140px] h-full">
                  <div className="absolute top-0 right-0 p-3 opacity-15 transform translate-x-4 -translate-y-4">
                    <Sparkles className="h-24 w-24" />
                  </div>
                  <div className="relative flex items-center gap-2.5 shrink-0">
                    <div className="w-8 h-8 bg-white/20 backdrop-blur-md rounded-lg flex items-center justify-center border border-white/20">
                      <Zap className="h-4.5 w-4.5" />
                    </div>
                    <div>
                      <h4 className="text-[9px] font-black uppercase tracking-[0.15em] opacity-80">
                        {t("Agent Generated")}
                      </h4>
                      <p className="text-xs font-bold leading-none mt-0.5">
                        {t("Produção IA")}
                      </p>
                    </div>
                  </div>
                  <div className="my-1.5 flex-1 flex flex-col justify-center">
                    <div className="text-3xl font-black tracking-tight leading-none">
                      {(() => {
                        const allPosts = [
                          ...(Array.isArray(pendingPosts) ? pendingPosts : []),
                          ...(Array.isArray(approvedPosts)
                            ? approvedPosts
                            : []),
                          ...(Array.isArray(publishedPosts)
                            ? publishedPosts
                            : []),
                          ...(Array.isArray(scheduledPosts)
                            ? scheduledPosts
                            : []),
                        ];
                        return allPosts.filter((post) => post.agent_generated)
                          .length;
                      })()}
                    </div>
                    <p className="text-[10px] font-medium opacity-80 leading-normal mt-1">
                      {t("Materiais criados autonomamente.")}
                    </p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-md rounded-lg p-1.5 border border-white/10 flex items-center justify-between text-[10px] shrink-0">
                    <span className="font-bold opacity-75">
                      {t("Penetração IA")}
                    </span>
                    <span className="font-black italic">
                      {(() => {
                        const total =
                          (stats?.pending || 0) +
                          (stats?.approved || 0) +
                          (stats?.published || 0) +
                          (stats?.scheduled || 0);
                        const aiGenerated = (() => {
                          const allPosts = [
                            ...(Array.isArray(pendingPosts)
                              ? pendingPosts
                              : []),
                            ...(Array.isArray(approvedPosts)
                              ? approvedPosts
                              : []),
                            ...(Array.isArray(publishedPosts)
                              ? publishedPosts
                              : []),
                            ...(Array.isArray(scheduledPosts)
                              ? scheduledPosts
                              : []),
                          ];
                          return allPosts.filter((post) => post.agent_generated)
                            .length;
                        })();
                        return total > 0
                          ? `${((aiGenerated / total) * 100).toFixed(1)}%`
                          : "0%";
                      })()}
                    </span>
                  </div>
                </div>

                {/* Card de Taxa de Aprovação */}
                <div className="bg-white rounded-xl border border-purple-200 shadow-xs hover:shadow-md transition-all p-3 flex flex-col justify-between min-h-[140px] h-full">
                  <div className="flex items-center justify-between shrink-0">
                    <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 border border-purple-200 flex items-center justify-center">
                      <CheckCircle className="h-4.5 w-4.5" />
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-black text-gray-900 leading-none">
                        {(() => {
                          const approved = stats?.approved || 0;
                          const rejected = stats?.rejected || 0;
                          const totalEvaluated = approved + rejected;
                          return totalEvaluated > 0
                            ? ((approved / totalEvaluated) * 100).toFixed(1)
                            : "0";
                        })()}
                        %
                      </div>
                      <div className="text-[9px] font-black text-gray-400 uppercase tracking-widest mt-0.5">
                        {t("Eficácia")}
                      </div>
                    </div>
                  </div>
                  <div className="flex-1 flex flex-col justify-center my-1">
                    <h4 className="text-xs font-bold text-gray-900 leading-tight flex items-center gap-1.5">
                      {t("Taxa de Aprovação")}
                      <Tooltip
                        content={t(
                          "Calculado com base no total de posts gerados pela IA que já passaram por triagem (Aprovados ÷ (Aprovados + Rejeitados)).",
                        )}
                        position="top"
                        wrapperClassName="flex shrink-0"
                      >
                        <Info className="h-3 w-3 text-gray-400 hover:text-purple-600 transition-colors cursor-help" />
                      </Tooltip>
                    </h4>
                    <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">
                      {t("Humano vs IA")}
                    </p>
                  </div>
                  <div className="shrink-0">
                    <div className="w-full h-1.5 bg-gray-50 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full bg-emerald-500 transition-all duration-1500"
                        style={{
                          width: `${(() => {
                            const approved = stats?.approved || 0;
                            const rejected = stats?.rejected || 0;
                            const totalEvaluated = approved + rejected;
                            return totalEvaluated > 0
                              ? (approved / totalEvaluated) * 100
                              : 0;
                          })()}%`,
                        }}
                      />
                    </div>
                    <p className="text-[9px] font-black text-gray-400 mt-1.5 uppercase tracking-widest leading-none">
                      {stats?.approved || 0} {t("de")}{" "}
                      {(stats?.approved || 0) + (stats?.rejected || 0)}{" "}
                      {t("validados")}
                    </p>
                  </div>
                </div>

                {/* Card de Taxa de Publicação */}
                <div className="bg-white rounded-xl border border-purple-200 shadow-xs hover:shadow-md transition-all p-3 flex flex-col justify-between min-h-[140px] h-full">
                  <div className="flex items-center justify-between shrink-0">
                    <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 border border-purple-200 flex items-center justify-center">
                      <TrendingUp className="h-4.5 w-4.5" />
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-black text-gray-900 leading-none">
                        {(() => {
                          const published = stats?.published || 0;
                          const total =
                            (stats?.pending || 0) +
                            (stats?.approved || 0) +
                            (stats?.published || 0) +
                            (stats?.rejected || 0) +
                            (stats?.scheduled || 0);
                          return total > 0
                            ? ((published / total) * 100).toFixed(1)
                            : "0";
                        })()}
                        %
                      </div>
                      <div className="text-[9px] font-black text-gray-400 uppercase tracking-widest mt-0.5">
                        {t("Eficácia")}
                      </div>
                    </div>
                  </div>
                  <div className="flex-1 flex flex-col justify-center my-1">
                    <h4 className="text-xs font-bold text-gray-900 leading-tight flex items-center gap-1.5">
                      {t("Taxa de Publicação")}
                      <Tooltip
                        content={t(
                          "Percentual do total de materiais produzidos pela IA que chegaram ao status 'Publicado'.",
                        )}
                        position="top"
                        wrapperClassName="flex shrink-0"
                      >
                        <Info className="h-3 w-3 text-gray-400 hover:text-blue-600 transition-colors cursor-help" />
                      </Tooltip>
                    </h4>
                    <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">
                      {t("Presença Digital")}
                    </p>
                  </div>
                  <div className="shrink-0">
                    <div className="w-full h-1.5 bg-gray-50 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full bg-blue-500 transition-all duration-1500"
                        style={{
                          width: `${(() => {
                            const published = stats?.published || 0;
                            const total =
                              (stats?.pending || 0) +
                              (stats?.approved || 0) +
                              (stats?.published || 0) +
                              (stats?.rejected || 0) +
                              (stats?.scheduled || 0);
                            return total > 0 ? (published / total) * 100 : 0;
                          })()}%`,
                        }}
                      />
                    </div>
                    <p className="text-[9px] font-black text-gray-400 mt-1.5 uppercase tracking-widest leading-none">
                      {stats?.published || 0} {t("materiais em live")}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Calendário de Distribuição */}
            <div className="lg:col-span-4 flex flex-col gap-2 bg-white rounded-xl border border-purple-200 p-3 shadow-xs">
              {scheduleLoading ? (
                // Mini-skeleton inline enquanto os dados carregam
                <div className="animate-pulse flex flex-col gap-3">
                  <div className="flex items-start gap-3 border-b pb-2 border-purple-100">
                    <div className="w-10 h-10 bg-indigo-100 rounded-xl shrink-0" />
                    <div className="flex-1 h-10 flex flex-col justify-between">
                      <div className="h-3.5 w-36 bg-gray-300 rounded-sm" />
                      <div className="h-2.5 w-24 bg-gray-200 rounded-sm" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {[...Array(4)].map((_, i) => (
                      <div
                        key={i}
                        className="rounded-xl border border-purple-100 p-3 min-h-[96px] flex flex-col gap-2"
                      >
                        <div className="w-8 h-8 bg-gray-200 rounded-lg" />
                        <div className="h-4 w-10 bg-gray-300 rounded-sm" />
                        <div className="h-2 w-14 bg-gray-200 rounded-sm" />
                      </div>
                    ))}
                  </div>
                </div>
              ) : scheduleOverview ? (
                <>
                  <SectionHeader
                    icon={<Calendar className="h-5 w-5" />}
                    title={t("Calendário de Distribuição")}
                    subtitle={t("Eficiência de Grade")}
                    iconBgClass="bg-indigo-600"
                  />

                  <div className="grid grid-cols-2 gap-2 shrink-0">
                    {[
                      {
                        title: t("Total Agendados"),
                        value: scheduleOverview.statistics?.total_posts || 0,
                        icon: Calendar,
                        color: "purple",
                        sub: t("Fila futura"),
                      },
                      {
                        title: t("Canais Ativos"),
                        value:
                          scheduleOverview.statistics?.total_platforms || 0,
                        icon: Target,
                        color: "emerald",
                        sub: t("Redes ativas"),
                      },
                      {
                        title: (
                          <div className="flex items-center gap-1">
                            {t("Ritmo Médio")}
                            <Tooltip
                              content={t(
                                "Média de distribuição por semana de acordo com o intervalo do Período Ativo.",
                              )}
                              position="top"
                              wrapperClassName="flex shrink-0"
                            >
                              <Info className="h-3 w-3 text-gray-400 hover:text-blue-600 transition-colors cursor-help" />
                            </Tooltip>
                          </div>
                        ),
                        value:
                          scheduleOverview.statistics?.average_posts_per_week?.toFixed(
                            1,
                          ) || "0.0",
                        icon: TrendingUp,
                        color: "blue",
                        sub: t("Posts / semana"),
                      },
                      {
                        title: t("Período Ativo"),
                        value: scheduleOverview.statistics?.date_range?.start
                          ? `${new Date(scheduleOverview.statistics.date_range.start).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })}`
                          : "---",
                        icon: Clock,
                        color: "orange",
                        sub: scheduleOverview.statistics?.date_range?.end
                          ? `${t("Até")} ${new Date(scheduleOverview.statistics.date_range.end).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })}`
                          : t("Aberto"),
                      },
                    ].map((item, idx) => (
                      <Card
                        key={idx}
                        variant="default"
                        size="sm"
                        className="border-purple-200! shadow-xs hover:shadow-md transition-all flex flex-col [&>div]:flex-1 [&>div]:flex [&>div]:flex-col [&>div]:justify-between [&>div]:gap-2 min-h-[96px] overflow-hidden"
                      >
                        <div className="relative flex items-center gap-2 shrink-0">
                          <div
                            className={cn(
                              "w-8 h-8 rounded-lg flex items-center justify-center border shrink-0",
                              item.color === "purple"
                                ? "bg-purple-50 text-purple-600 border-purple-200"
                                : item.color === "emerald"
                                  ? "bg-emerald-50 text-emerald-600 border-purple-200"
                                  : item.color === "blue"
                                    ? "bg-blue-50 text-blue-600 border-purple-200"
                                    : "bg-orange-50 text-orange-600 border-purple-200",
                            )}
                          >
                            <item.icon className="h-4.5 w-4.5" />
                          </div>
                          <div>
                            <div className="text-xl font-black text-gray-900 tracking-tighter leading-none">
                              {item.value}
                            </div>
                            <div className="text-[9px] font-black uppercase tracking-widest text-gray-400 mt-0.5">
                              {item.title}
                            </div>
                          </div>
                        </div>
                        <div className="mt-1 pt-1.5 border-t border-purple-100 flex items-center justify-between shrink-0">
                          <span className="text-[9px] font-bold text-gray-400 uppercase tracking-tight truncate max-w-[80px]">
                            {item.sub}
                          </span>
                          <div className="flex gap-0.5">
                            {[1, 2, 3].map((i) => (
                              <div
                                key={i}
                                className={cn(
                                  "w-1 h-1 rounded-full",
                                  item.color === "purple"
                                    ? "bg-purple-200"
                                    : item.color === "emerald"
                                      ? "bg-emerald-200"
                                      : item.color === "blue"
                                        ? "bg-blue-200"
                                        : "bg-orange-200",
                                )}
                              />
                            ))}
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                </>
              ) : (
                // Estado vazio: nenhum post agendado
                <div className="flex flex-col items-center justify-center flex-1 py-6 text-center gap-2">
                  <Calendar className="h-8 w-8 text-indigo-200" />
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                    {t("Sem agendamentos")}
                  </p>
                  <p className="text-[10px] text-gray-300">
                    {t("Nenhum post agendado para o período")}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Linha 2: Ranking de Posts (Largura Total - 12/12) */}
          <div className="bg-white rounded-xl border border-purple-200 p-3 shadow-xs">
            <PostRanking
              posts={sortedPosts}
              loading={postsLoading}
              error={postsError}
              sortBy={sortBy}
              setSortBy={setSortBy}
              fetchPosts={fetchPosts}
            />
          </div>

          {/* Linha 3: Performance por Canal (Largura Total - 12/12) */}
          <div className="bg-white rounded-xl border border-purple-200 p-3 shadow-xs">
            <PlatformDashboard
              stats={platformStats}
              gmb={gmbData}
              gmbLoading={gmbLoading}
              onSyncDone={fetchPosts}
            />
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default PostsAnalytics;
